import { MetricField, MetricUnit } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { getUnitTranslation } from './getUnitTranslation';

export const getUnitsPerMetric = (metric: MetricField, i18n: I18n) => {
  if ([MetricField.Flow, MetricField.FlowRaw, MetricField.DgaFlow].includes(metric))
    return [
      {
        value: 'liter-per-second',
        enumValue: MetricUnit.LiterPerSecond,
        label: getUnitTranslation('liter-per-second', 'short', i18n),
      },
      {
        value: 'cubic-meter-per-second',
        enumValue: MetricUnit.CubicMeterPerSecond,
        label: getUnitTranslation('cubic-meter-per-second', 'short', i18n),
      },
      {
        value: 'cubic-meter-per-hour',
        enumValue: MetricUnit.CubicMeterPerHour,
        label: getUnitTranslation('cubic-meter-per-hour', 'short', i18n),
      },
    ];
  if (
    [
      MetricField.Level,
      MetricField.OriginalDistance,
      MetricField.OriginalDistanceRaw,
      MetricField.Position,
      MetricField.LevelRaw,
      MetricField.SnowLevel,
      MetricField.SnowLevelRaw,
      MetricField.SnowLevelNonCalibrated,
    ].includes(metric)
  )
    return [
      {
        value: 'millimeter',
        enumValue: MetricUnit.Millimeter,
        label: getUnitTranslation('millimeter', 'short', i18n),
      },
      {
        value: 'centimeter',
        enumValue: MetricUnit.Centimeter,
        label: getUnitTranslation('centimeter', 'short', i18n),
      },
      {
        value: 'meter',
        enumValue: MetricUnit.Meter,
        label: getUnitTranslation('meter', 'short', i18n),
      },
    ];
  if (metric === MetricField.Angle)
    return [
      {
        value: 'degree',
        enumValue: MetricUnit.Degree,
        label: getUnitTranslation('degree', 'short', i18n),
      },
    ];
  if (metric === MetricField.Velocity)
    return [
      {
        value: 'centimeter-per-second',
        enumValue: MetricUnit.CentimeterPerSecond,
        label: getUnitTranslation('centimeter-per-second', 'short', i18n),
      },
      {
        value: 'millimeter-per-second',
        enumValue: MetricUnit.MillimeterPerSecond,
        label: getUnitTranslation('millimeter-per-second', 'short', i18n),
      },
    ];
  if (
    [
      MetricField.VolumeM3,
      MetricField.VolumeDay,
      MetricField.VolumeHour,
      MetricField.VolumeMonth,
      MetricField.StoredVolume,
      MetricField.CalculatedVolume,
      MetricField.AccumulatedVol,
      MetricField.AccumulatedVolRaw,
      MetricField.OriginalVol,
      MetricField.OriginalVolRaw,
      MetricField.AccumulatedVolume,
      MetricField.DgaAccumulatedVolume,
    ].includes(metric)
  )
    return [
      {
        value: 'liter',
        enumValue: MetricUnit.Liter,
        label: getUnitTranslation('liter', 'short', i18n),
      },
      {
        value: 'cubic-meter',
        enumValue: MetricUnit.CubicMeter,
        label: getUnitTranslation('cubic-meter', 'short', i18n),
      },
      {
        value: 'cubic-hectometer',
        enumValue: MetricUnit.CubicHectometer,
        label: getUnitTranslation('cubic-hectometer', 'short', i18n),
      },
    ];

  if (
    [
      MetricField.Temperature,
      MetricField.TemperatureRaw,
      MetricField.SnowTemperature,
      MetricField.SnowTemperatureRaw,
    ].includes(metric)
  )
    return [
      {
        value: 'celsius',
        enumValue: MetricUnit.Celsius,
        label: getUnitTranslation('celsius', 'short', i18n),
      },
      {
        value: 'fahrenheit',
        enumValue: MetricUnit.Fahrenheit,
        label: getUnitTranslation('fahrenheit', 'short', i18n),
      },
    ];

  if (metric === MetricField.BatterySoc || metric === MetricField.BatterySocRaw)
    return [
      {
        value: 'percent',
        enumValue: MetricUnit.Percent,
        label: getUnitTranslation('percent', 'short', i18n),
      },
    ];

  if (metric === MetricField.Conductivity || metric === MetricField.ConductivityRaw)
    return [
      {
        value: 'microsiemens-per-centimeter',
        enumValue: MetricUnit.MicrosiemensPerCentimeter,
        label: getUnitTranslation('microsiemens-per-centimeter', 'short', i18n),
      },
      {
        value: 'millisiemens-per-centimeter',
        enumValue: MetricUnit.MillisiemensPerCentimeter,
        label: getUnitTranslation('millisiemens-per-centimeter', 'short', i18n),
      },
    ];

  if (metric === MetricField.Ph || metric === MetricField.PhRaw)
    return [
      {
        value: 'ph',
        enumValue: MetricUnit.Ph,
        label: getUnitTranslation('ph', 'short', i18n),
      },
    ];

  if (metric === MetricField.TurbidityNtu || metric === MetricField.TurbidityNtuRaw)
    return [
      {
        value: 'ntu',
        enumValue: MetricUnit.Ntu,
        label: getUnitTranslation('ntu', 'short', i18n),
      },
    ];

  if (metric === MetricField.TurbidityFnu || metric === MetricField.TurbidityFnuRaw)
    return [
      {
        value: 'fnu',
        enumValue: MetricUnit.Fnu,
        label: getUnitTranslation('fnu', 'short', i18n),
      },
    ];

  if (
    metric === MetricField.SolarV ||
    metric === MetricField.SolarVRaw ||
    metric === MetricField.BatteryV ||
    metric === MetricField.BatteryVRaw
  )
    return [
      {
        value: 'volt',
        enumValue: MetricUnit.Volt,
        label: getUnitTranslation('volt', 'short', i18n),
      },
      {
        value: 'millivolt',
        enumValue: MetricUnit.Millivolt,
        label: getUnitTranslation('millivolt', 'short', i18n),
      },
    ];

  if (
    metric === MetricField.SolarI ||
    metric === MetricField.SolarIRaw ||
    metric === MetricField.ChargeI ||
    metric === MetricField.ChargeIRaw
  )
    return [
      {
        value: 'ampere',
        enumValue: MetricUnit.Ampere,
        label: getUnitTranslation('ampere', 'short', i18n),
      },
    ];

  if (
    metric === MetricField.SolarP ||
    metric === MetricField.SolarPRaw ||
    metric === MetricField.ChargeP ||
    metric === MetricField.ChargePRaw
  )
    return [
      {
        value: 'watt',
        enumValue: MetricUnit.Watt,
        label: getUnitTranslation('watt', 'short', i18n),
      },
    ];

  return [];
};
